// SETTLE LATER's small pieces (UI rebuild batch 3, screens/v2/Settle.tsx). The model is today's (operator, 26 Oct 2026,
// via the Command Centre: "keep today's per-party model and posting, in Astra's lighter look"): the unit is the PARTY
// BOX, a settle-later trade's open legs are its explanation, and settling is ordinary money in / out — Record money,
// prefilled with the party, the leg's asset, its OPEN figure and the way it moves. No deal-scoped form, no new posting.
//
// The writes here — Hold leg ("still to receive / pay") and Reopen — are copied from screens/Settlements.tsx (HoldLink,
// SweptRow) with their confirms, their mutations and their refusals unchanged; only the drawing is lighter, and a
// refusal reads through the kit's ServerLine (a 5xx may have landed: its sentence says so).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews, type DealRow, type LegState, type SettlementsResponse } from '../../../lib/api';
import { displayAsset, fmtAmount } from '../../../lib/format';
import { partyName, rodeSentence } from '../../../lib/naming';
import { ServerLine } from '../kit';
import { MoneyText } from '../MoneyText';

export type Legs = { take: LegState; give: LegState };
export type Side = 'take' | 'give';

/** RECORD MONEY, PREFILLED FROM ONE LEG — the params RecordMoney reads today (party, dir, asset, amount; no rail, so
 *  its picker asks which account the money moved through). The figure is the leg's OPEN figure, never the party's box
 *  (4 Sept: 15,705 INR was left behind when the box figure was posted for a leg). take = they pay us (in), give = we
 *  pay them (out). */
export function settleHref(partySlug: string, side: Side, leg: LegState): string {
  const dir = side === 'take' ? 'in' : 'out';
  return `/record?party=${encodeURIComponent(partySlug)}&dir=${dir}&asset=${encodeURIComponent(leg.asset)}&amount=${leg.open}`;
}

/** The legs still open on a deal, take first. A leg the arithmetic calls done but he has held stays here (open may be
 *  0 — it waits for real money); its door shows only when there is a figure to prefill. */
export function openLegsOf(legs: Legs | null): Array<{ side: Side; leg: LegState }> {
  if (!legs) return [];
  return (['take', 'give'] as const).filter((s) => !legs[s].settled).map((s) => ({ side: s, leg: legs[s] }));
}

/** The verb on the door (Astra's next-step words): money in is Receive, money out is Pay out. */
export const doorVerb = (side: Side): string => (side === 'take' ? 'Receive' : 'Pay out');
/** Whose side the remaining money is on — the customer's (Books rule: "Customer pays / receives"). */
export const sideWords = (side: Side): string => (side === 'take' ? 'Customer pays' : 'Customer receives');

/** The trade from the customer's side, without the party (the party heads the group): "Buys 18,400.5 USDT · pays
 *  67,529.84 AED". Settlements.tsx's dealWords, less the name. */
export function tradeWords(d: Pick<DealRow, 'direction' | 'amount_take' | 'asset_take' | 'amount_give' | 'asset_give'>): string {
  const take = `${fmtAmount(d.amount_take, d.asset_take)} ${displayAsset(d.asset_take)}`;
  const give = `${fmtAmount(d.amount_give, d.asset_give)} ${displayAsset(d.asset_give)}`;
  return d.direction === 'customer_buy' ? `Buys ${give} · pays ${take}` : `Sells ${take} · gets ${give}`;
}

/** The same sentence drawn, each figure and its unit one unbreakable money piece (mono, tabular) — on a screen; tradeWords
 *  stays the plain string for search and titles. */
export function TradeSentence({ d }: { d: Pick<DealRow, 'direction' | 'amount_take' | 'asset_take' | 'amount_give' | 'asset_give'> }) {
  const fig = (v: string | number, a: string) => (
    <span className="whitespace-nowrap"><span className="amt">{fmtAmount(v, a)}</span> {displayAsset(a)}</span>
  );
  return d.direction === 'customer_buy'
    ? <>Buys {fig(d.amount_give, d.asset_give)} · pays {fig(d.amount_take, d.asset_take)}</>
    : <>Sells {fig(d.amount_take, d.asset_take)} · gets {fig(d.amount_give, d.asset_give)}</>;
}

/** THE DOOR: Record money prefilled from one open leg — a tinted chip (blue in, amber out) as Astra's next step, at
 *  the phone's 44 px and the laptop's 40 px. */
export function SettleDoor({ partySlug, side, leg, withFigure = false, primary = false }: {
  partySlug: string; side: Side; leg: LegState; withFigure?: boolean; primary?: boolean;
}) {
  const label = withFigure ? `${doorVerb(side)} ${fmtAmount(leg.open, leg.asset)} ${displayAsset(leg.asset)}` : doorVerb(side);
  const tone = primary ? 'btn-primary px-4' : side === 'take' ? 'bg-info-bg text-info' : 'bg-warn-bg text-warn';
  return (
    <Link to={settleHref(partySlug, side, leg)}
      title={`${doorVerb(side)} ${fmtAmount(leg.open, leg.asset)} ${displayAsset(leg.asset)}`}
      className={`inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg px-3 text-body font-medium hover:opacity-90 lg:min-h-10 ${tone}`}>
      {label}
    </Link>
  );
}

/** "still to receive" / "still to pay" beside a leg the arithmetic calls settled — his word that money is still
 *  expected (3 Sept, HK's coins). A write that reopens the trade: it asks once and never fails in silence.
 *  Settlements.tsx HoldLink, unchanged but for the drawing. */
export function HoldLink({ dealId, leg }: { dealId: string; leg: Side }) {
  const qc = useQueryClient();
  const m = useMutation({ mutationFn: () => api.holdLeg(dealId, [leg]), onSuccess: () => refreshMoneyViews(qc) });
  const verb = leg === 'take' ? 'Still to receive' : 'Still to pay';
  return (
    <span className="block">
      <button type="button" className="btn-link text-meta max-lg:py-3.5 max-lg:-my-3.5" disabled={m.isPending}
        onClick={() => {
          if (confirm(`${verb} on ${dealId}? The trade reopens — only a receipt or payment closes it.`)) m.mutate();
        }}>
        {m.isPending ? 'holding…' : verb.toLowerCase()}
      </button>
      {m.isError && <ServerLine error={m.error} className="mt-1 text-left" />}
    </span>
  );
}

/** THE TRADES THIS READ CLOSED — the only time he is told, with what RODE on the running account (3-4 Sept ruling).
 *  One quiet panel: party and the rode figure on the line; the full id and Reopen one tap down. */
export function SweptPanel({ swept }: { swept: SettlementsResponse['swept'] }) {
  if (swept.length === 0) return null;
  return (
    <section aria-label="Closed on this read" className="mb-4 rounded-[10px] border border-stroke bg-panel px-4 py-3 lg:mb-5 lg:px-5">
      <h2 className="text-body text-pos">{swept.length} closed on this read</h2>
      <ul className="mt-1">
        {swept.map((s) => <SweptRow key={s.deal_id} deal={s} />)}
      </ul>
    </section>
  );
}

/** THE UNDO for a trade the sweep just closed. Only trades THIS read closed are offered: an ordinary instant trade is
 *  also 'settled', and a general Reopen would turn a finished trade into an open position (Settlements.tsx SweptRow). */
function SweptRow({ deal }: { deal: SettlementsResponse['swept'][number] }) {
  const qc = useQueryClient();
  const m = useMutation({ mutationFn: () => api.reopenDeal(deal.deal_id), onSuccess: () => refreshMoneyViews(qc) });
  const rode = rodeSentence(deal.rode);
  const [more, setMore] = useState(false);
  return (
    <li className="border-t border-stroke-soft py-1 text-body first:border-t-0 lg:py-2">
      {/* A SHORT LIST, figures in their own right-hand column (Astra's audit P1, 1 Nov 2026: the counts and amounts were packed
          into sentences): the party · what rode on the running account, figure then its muted unit · Details. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4">
        <span className="min-w-0 break-words">{partyName({ slug: deal.party_slug })}</span>
        <span className="flex items-baseline gap-x-4">
          {deal.rode.filter((r) => r.amount !== 0).map((r) => (
            <span key={r.asset} className="whitespace-nowrap" title={rode}>
              <span className="font-mono tabular">{fmtAmount(Math.abs(r.amount), r.asset)}</span> <span className="text-tg-hint">{displayAsset(r.asset)} rode</span>
            </span>
          ))}
          <button type="button" aria-expanded={more} className="btn-link inline-flex min-h-11 items-center text-body lg:min-h-0" onClick={() => setMore((v) => !v)}>
            {more ? 'Hide' : 'Details'}
          </button>
        </span>
      </div>
      {more && (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
          <span className="break-all font-mono">{deal.deal_id}</span>
          <button type="button" className="btn-link inline-flex min-h-11 items-center text-body disabled:opacity-50 lg:min-h-0" disabled={m.isPending}
            onClick={() => {
              if (confirm(`Reopen ${deal.deal_id}? Only real money in or out will close it again.`)) m.mutate();
            }}>
            {m.isPending ? 'Reopening…' : 'Reopen'}
          </button>
        </div>
      )}
      {m.isError && <ServerLine error={m.error} className="mt-1" />}
    </li>
  );
}
